import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, CircularProgress, IconButton, Popover, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import { tokens } from '../theme';
import { useVoice } from './VoiceContext';
import { errorMessage } from './voiceApi';
import { canOpenCall, isUnassigned } from './callFormat';
import { readSeenFailures, countUnseenFailures, markFailuresSeen } from './seenFailures';
import ActivityCard from './ActivityCard';
import CallDetailDialog from './CallDetailDialog';
import ContactSearch from './ContactSearch';

/**
 * Top-bar bell for the call activity feed (plan §5.5 / §8). Every admin sees every card; opening a
 * card (details, attach, retry) is possible for the calls this admin placed, or for any call when
 * they are a manager (VOICE_ADMIN_EMAILS). The badge counts failures this admin has not yet seen —
 * keyed by the failure itself (step + message), so a note edit or a nightly re-run that changes
 * nothing does not re-light it.
 */
const ActivityBell = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const voice = useVoice();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState(null);
    const [tab, setTab] = useState(0);
    const [seen, setSeen] = useState(readSeenFailures);
    const [openCallId, setOpenCallId] = useState(null);
    const [attaching, setAttaching] = useState(null); // card id whose search is open

    const feeds = voice?.feeds;
    const recent = feeds?.recent.cards || [];
    const unassigned = feeds?.unassigned.cards || [];
    const permitted = Boolean(voice?.permitted);
    const open = Boolean(anchorEl);

    const unseenFailures = useMemo(() => countUnseenFailures(recent, seen), [recent, seen]);

    // Opening the panel marks every current failure as seen; the Unassigned tab is loaded only while shown.
    useEffect(() => {
        if (!open) { voice?.setUnassignedWanted?.(false); return; }
        setSeen(markFailuresSeen(recent, readSeenFailures())); // read fresh: the softphone tab may have marked some
        voice?.setUnassignedWanted?.(tab === 1);
    }, [open, tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const attach = async (card, { student, teacher }) => {
        try {
            await voice.attachCall(card.id, teacher ? { teacher_uuid: teacher.uuid } : { student_uuid: student.uuid });
            setAttaching(null);
            const who = teacher || student;
            enqueueSnackbar(`Call #${card.id} attached to ${who.first_name} ${who.last_name}`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    };

    const retry = async (card) => {
        try {
            const { action } = await voice.retryCall(card.id);
            enqueueSnackbar(action === 'transcribe' ? `Transcribing call #${card.id} again…` : `Fetching the recording for call #${card.id} again…`, { variant: 'info' });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    };

    if (!voice) return null;
    const loading = feeds.recent.loading || feeds.unassigned.loading;
    const openable = (card) => canOpenCall(voice.me, card);
    const list = tab === 0 ? recent : unassigned;
    const loadError = tab === 0 ? feeds.recent.error : feeds.unassigned.error;

    return (
        <>
            <Tooltip title="Calls">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Badge badgeContent={unseenFailures} color="error" max={99}>
                        <PhoneInTalkOutlinedIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disableEnforceFocus // the softphone can be used while the panel is open
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                sx={{ '& .MuiPopover-paper': { borderRadius: '10px', border: '1px solid', backgroundColor: colors.primary[750], width: 520, maxHeight: '75vh' } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1, pb: 0 }}>
                    <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ flex: 1, minHeight: 40 }}>
                        <Tab label="Recent" sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label={`Unassigned${unassigned.length ? ` (${unassigned.length})` : ''}`} sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>
                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    <IconButton size="small" onClick={() => feeds.refreshAll(true)}><RefreshIcon fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ px: 2, pt: 1.5, pb: 2, overflowY: 'auto', maxHeight: 'calc(75vh - 56px)' }}>
                    {tab === 1 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Calls to numbers no student or teacher has on file. Attach each one to the right person so the recording and transcript land on their record.
                        </Typography>
                    )}
                    {loadError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{loadError}</Typography>}
                    {!loadError && list.length === 0 && !loading && (
                        <Typography variant="body2" color="text.secondary">{tab === 0 ? 'No calls yet.' : 'Nothing to attach.'}</Typography>
                    )}
                    {list.map((card) => (
                        <Box key={card.id}>
                            <ActivityCard card={card} onOpen={openable(card) ? () => setOpenCallId(card.id) : undefined} onRetry={openable(card) ? retry : undefined} />
                            {isUnassigned(card) && openable(card) && (
                                <Box sx={{ mt: -0.5, mb: 1.5, ml: 1 }}>
                                    {attaching === card.id ? (
                                        <ContactSearch mode="attach" autoFocus placeholder="Attach to a student or teacher…" onPick={(pick) => attach(card, pick)} />
                                    ) : (
                                        <Button size="small" variant="outlined" onClick={() => setAttaching(card.id)} sx={{ textTransform: 'none', py: 0 }}>
                                            Attach to a student or teacher
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                    {tab === 0 && !voice.manager && recent.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {permitted ? 'You can open the calls you placed. Other admins\u2019 calls are listed for reference.' : 'Recordings and transcripts are visible to admins with the call permission.'}
                        </Typography>
                    )}
                </Box>
            </Popover>
            <CallDetailDialog callId={openCallId} open={openCallId !== null} onClose={() => setOpenCallId(null)} />
        </>
    );
};

export default ActivityBell;
